import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Device } from "../../../../../../API";
import { useTimeZoneFromLocationId } from "../../../../../../common/hooks/useTimeZoneFromLocationId";
import { handleApolloError } from "../../../../../../common/utils/handleApolloError";
import { errorNotification } from "../../../../../../common/variables/notification";
import { useGetTimelapseVideo } from "../../../../../telemetry/hooks/useGetTimelapseVideo";
import { useTimelapseAvailableDates } from "../../../../../telemetry/hooks/useTimelapseAvailableDates";

dayjs.extend(utc);

dayjs.extend(timezone);

const toTimeZoneStartOfDay = (date: Date, timeZone?: string): Date => {
  if (!timeZone) {
    return dayjs(date).startOf("day").toDate();
  }

  return dayjs(date).tz(timeZone).startOf("day").toDate();
};

const formatDatePath = (
  selectedDate: Date | null,
  timeZone?: string
): string => {
  if (!selectedDate) return "";

  // return the input date in the format YYYY/MM/DD; timezone is already accounted for in available dates in picker
  return dayjs(selectedDate).format("YYYY/MM/DD");
};

const extractDownloadUrl = (manifest: string): string | null => {
  if (!manifest) return null;

  const matches = manifest.match(/https?:\/\/[^\s]+/g);

  if (!matches || matches.length === 0) {
    return null;
  }

  const fullDayUrl = matches.find(url => url.toLowerCase().includes("fullday"));

  return fullDayUrl ?? matches[matches.length - 1] ?? null;
};

type UseLiveViewTimelapseState = {
  selectedDate: Date | null;
  timeZone?: string;
  minSelectableDate: Date;
  maxSelectableDate: Date;
  timelapseHlsUrl: string | null;
  statusMessage: string;
  loading: boolean;
  downloading: boolean;
  isDownloadDisabled: boolean;
  isDateSelectable: (date: Date) => boolean;
  handleDateChange: (date: Date | null) => void;
  handleDownloadClick: () => Promise<void>;
};

export const useLiveViewTimelapseState = (
  device: Device
): UseLiveViewTimelapseState => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [timelapseHlsUrl, setTimelapseHlsUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const timeZone = useTimeZoneFromLocationId(device.locationId);

  const deviceIdClean = device?.id.split("DE#")[1]?.replaceAll("#", "_") ?? "";
  const nodeIdClean = device?.nodeId.replaceAll("#", "_") ?? "";

  const {
    availableDatePaths,
    availableDateSet,
    loading: availableDatesLoading,
  } = useTimelapseAvailableDates(deviceIdClean, nodeIdClean);

  const availableDates = useMemo(() => {
    if (!availableDatePaths?.length) {
      return [];
    }

    return availableDatePaths
      .map(path => {
        const parts = path.split("/");

        if (parts.length !== 3) return null;

        const [yearStr, monthStr, dayStr] = parts;

        const year = Number(yearStr);
        const month = Number(monthStr);
        const day = Number(dayStr);

        if (
          !Number.isFinite(year) ||
          !Number.isFinite(month) ||
          !Number.isFinite(day)
        ) {
          return null;
        }

        const utcDate = new Date(Date.UTC(year, month - 1, day));

        return toTimeZoneStartOfDay(utcDate, timeZone);
      })
      .filter(Boolean) as Date[];
  }, [availableDatePaths, timeZone]);

  const minSelectableDate = useMemo(() => {
    const referenceDate = toTimeZoneStartOfDay(new Date(), timeZone);
    // fallback to 1 year ago
    const fallback = new Date(
      referenceDate.getTime() - 365 * 24 * 60 * 60 * 1000
    );

    if (availableDates.length === 0) {
      return fallback;
    }

    const earliest = availableDates[0];

    return earliest < fallback ? earliest : fallback;
  }, [availableDates, timeZone]);

  const maxSelectableDate = useMemo(() => {
    const fallback = timeZone
      ? dayjs(dayjs().tz(timeZone).format("YYYY-MM-DD")).endOf("day").toDate()
      : dayjs.utc().endOf("day").toDate();

    if (availableDatePaths.length === 0) {
      return fallback;
    }

    // return the date from BE as-is; this is the only available date existing in the S3 bucket
    const latest = new Date(availableDatePaths[availableDatePaths.length - 1]);

    return latest;
  }, [availableDatePaths, timeZone]);

  const selectedDatePath = useMemo(
    () => formatDatePath(selectedDate, timeZone),
    [selectedDate, timeZone]
  );

  const {
    data: timelapseData,
    playlist,
    loading,
    error,
  } = useGetTimelapseVideo(deviceIdClean, nodeIdClean, selectedDatePath, {
    skip: !selectedDate || !deviceIdClean || !nodeIdClean,
  });

  const isDateSelectable = useCallback(
    (date: Date) => {
      if (!availableDateSet || availableDateSet.size === 0) {
        return false;
      }

      // dont need to reformat as BE already returns dates ready as is
      return availableDateSet.has(dayjs(date).format("YYYY-MM-DD"));
    },
    [availableDateSet, timeZone]
  );

  useEffect(() => {
    if (error) {
      handleApolloError(error);
    }
  }, [error]);

  useEffect(() => {
    if (!selectedDate) {
      setTimelapseHlsUrl(null);

      setDownloadUrl(null);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!playlist) {
      setTimelapseHlsUrl(null);

      setDownloadUrl(null);

      return;
    }

    try {
      const blob = new Blob([playlist], {
        type: "application/vnd.apple.mpegurl",
      });
      const url = URL.createObjectURL(blob);

      setTimelapseHlsUrl(url);

      setDownloadUrl(extractDownloadUrl(playlist));

      return () => URL.revokeObjectURL(url);
    } catch {
      setTimelapseHlsUrl(null);

      setDownloadUrl(null);
    }
  }, [playlist]);

  const hasAvailableDates = availableDateSet.size > 0;

  const statusMessage = useMemo(() => {
    if (loading) return "Loading timelapse...";

    if (!selectedDate) {
      if (availableDatesLoading) {
        return "Fetching available timelapse dates...";
      }

      if (!hasAvailableDates) {
        return "No timelapse clips available in the last year.";
      }

      return "Select a date to preview timelapse";
    }

    if (timelapseData?.message) return timelapseData.message;

    if (error) return "Failed to load timelapse. Please try again.";

    if (!timelapseHlsUrl) return "No timelapse clips available for this date.";

    return "";
  }, [
    loading,
    hasAvailableDates,
    availableDatesLoading,
    selectedDate,
    timelapseData?.message,
    error,
    timelapseHlsUrl,
  ]);

  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (!date) {
        setSelectedDate(null);

        setTimelapseHlsUrl(null);

        setDownloadUrl(null);

        return;
      }

      // we already account for timezone in the allowed date range, so when user selects a date, we can just use the date
      setSelectedDate(date);
      // const year = date.getFullYear();
      // const month = date.getMonth() + 1;
      // const day = date.getDate();
      // const normalizedDate = createDateInTimeZone(year, month, day, timeZone);
      // setSelectedDate(normalizedDate);

      setTimelapseHlsUrl(null);

      setDownloadUrl(null);
    },
    [timeZone]
  );

  const handleDownloadClick = useCallback(async () => {
    if (!downloadUrl) return;

    setDownloading(true);

    let urlObject: string | null = null;

    try {
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error("Failed to download timelapse video");
      }

      const blob = await response.blob();

      urlObject = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = urlObject;

      const formattedDateSegment = selectedDate
        ? dayjs(selectedDate).format("YYYY_MM_DD")
        : "";
      const filenameParts = [
        deviceIdClean || "timelapse",
        formattedDateSegment,
      ].filter(Boolean);

      link.setAttribute(
        "download",
        `${filenameParts.join("_") || "timelapse"}.mp4`
      );

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading timelapse video:", err);

      errorNotification(
        "Unable to download timelapse video. Please try again."
      );
    } finally {
      if (urlObject) URL.revokeObjectURL(urlObject);

      setDownloading(false);
    }
  }, [deviceIdClean, downloadUrl, selectedDate, timeZone]);

  // today relative to the facility timezone
  const isToday = useMemo(() => {
    if (!selectedDate) return false;

    // if the current date in facility timezone is the same as the selected date, then it is "today" i.e. the same day
    return (
      dayjs(selectedDate).format("YYYY-MM-DD") ===
      dayjs().tz(timeZone).format("YYYY-MM-DD")
    );
  }, [selectedDate, timeZone]);

  // if any condition is true, the download button should be disabled
  const isDownloadDisabled = !downloadUrl || downloading || loading || isToday;

  return {
    selectedDate,
    timeZone,
    minSelectableDate,
    maxSelectableDate,
    timelapseHlsUrl,
    statusMessage,
    loading: loading && !!selectedDate,
    downloading,
    isDownloadDisabled,
    isDateSelectable,
    handleDateChange,
    handleDownloadClick,
  };
};
